import { useState, useEffect, useCallback } from "react";
import { Hero } from "@/components/Hero";
import { DrawCard } from "@/components/DrawCard";
import { Footer } from "@/components/Footer";
import { WishDialog } from "@/components/WishDialog";
import { toast } from "@/hooks/use-toast";
import { useAccount, useChainId, useWalletClient } from "wagmi";
import { BrowserProvider } from "ethers";
import { initializeFHEVM, encryptNumber, decryptNumber, getFHEVMInstance } from "@/lib/fhevm";
import {
  createLottery,
  registerParticipant,
  drawWinner,
  getLotteryInfo,
  getWinningNumber,
  getLotteryCount,
  type LotteryInfo,
} from "@/lib/contract";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const Index = () => {
  const { address, isConnected } = useAccount();
  const chainId = useChainId();
  const { data: walletClient } = useWalletClient();
  
  const [lotteries, setLotteries] = useState<LotteryInfo[]>([]);
  const [selectedLotteryId, setSelectedLotteryId] = useState<bigint | null>(null);
  const [wishDialogOpen, setWishDialogOpen] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [lotteryName, setLotteryName] = useState("");
  const [maxParticipants, setMaxParticipants] = useState("10");
  const [isLoading, setIsLoading] = useState(false);
  const [fhevmReady, setFhevmReady] = useState(false);
  const [decryptingLotteryId, setDecryptingLotteryId] = useState<bigint | null>(null);

  // Initialize FHEVM when wallet is connected
  useEffect(() => {
    if (isConnected && chainId && walletClient) {
      initializeFHEVM(chainId)
        .then(() => {
          setFhevmReady(true);
          console.log("FHEVM initialized");
        })
        .catch((error) => {
          console.error("Failed to initialize FHEVM:", error);
          toast({
            title: "FHEVM Initialization Failed",
            description: error.message,
            variant: "destructive",
          });
        });
    } else {
      setFhevmReady(false);
    }
  }, [isConnected, chainId, walletClient]);

  // Load lotteries
  const loadLotteries = useCallback(async () => {
    if (!walletClient || !isConnected || !chainId) return;

    try {
      const provider = new BrowserProvider(walletClient as any);
      const count = await getLotteryCount(provider, chainId);
      const lotteryList: LotteryInfo[] = [];

      for (let i = 1; i <= Number(count); i++) {
        try {
          const info = await getLotteryInfo(provider, BigInt(i), chainId);
          lotteryList.push(info);
        } catch (error) {
          console.error(`Failed to load lottery ${i}:`, error);
        }
      }

      setLotteries(lotteryList);
    } catch (error) {
      console.error("Failed to load lotteries:", error);
      toast({
        title: "Failed to Load Lotteries",
        description: error instanceof Error ? error.message : "Unknown error",
        variant: "destructive",
      });
    }
  }, [walletClient, isConnected, chainId]);

  useEffect(() => {
    if (isConnected && walletClient && chainId) {
      loadLotteries();
      // Refresh every 10 seconds
      const interval = setInterval(loadLotteries, 10000);
      return () => clearInterval(interval);
    }
  }, [isConnected, walletClient, chainId, loadLotteries]);

  const handleCreateLottery = async () => {
    if (!walletClient || !lotteryName || !maxParticipants || !chainId) {
      toast({
        title: "Missing Information",
        description: "Please fill in all fields and ensure wallet is connected.",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);
    try {
      const provider = new BrowserProvider(walletClient as any);
      const signer = await provider.getSigner();
      const maxParts = parseInt(maxParticipants, 10);
      
      if (isNaN(maxParts) || maxParts <= 0) {
        throw new Error("Max participants must be a positive number");
      }

      await createLottery(signer, lotteryName, maxParts, chainId);
      toast({
        title: "Lottery Created",
        description: `Successfully created lottery: ${lotteryName}`,
      });
      setCreateDialogOpen(false);
      setLotteryName("");
      setMaxParticipants("10");
      await loadLotteries();
    } catch (error) {
      console.error("Failed to create lottery:", error);
      toast({
        title: "Failed to Create Lottery",
        description: error instanceof Error ? error.message : "Unknown error",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleParticipate = (lotteryId: bigint) => {
    setSelectedLotteryId(lotteryId);
    setWishDialogOpen(true);
  };

  const handleSubmitNumber = async (number: number) => {
    if (!walletClient || !selectedLotteryId || !fhevmReady || !chainId) {
      toast({
        title: "Not Ready",
        description: "Please wait for FHEVM to initialize and ensure wallet is connected.",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);
    try {
      const provider = new BrowserProvider(walletClient as any);
      const signer = await provider.getSigner();
      const { getContractAddress } = await import("@/lib/contract");
      const contractAddress = getContractAddress(chainId);
      
      if (!contractAddress) {
        throw new Error("Contract address not configured");
      }

      const fhevm = getFHEVMInstance();
      if (!fhevm) {
        throw new Error("FHEVM instance not available");
      }

      // Encrypt the number
      const encrypted = await encryptNumber(fhevm, contractAddress, address!, number);
      
      // Register participant
      await registerParticipant(
        signer,
        selectedLotteryId,
        encrypted.handles[0],
        encrypted.inputProof,
        chainId
      );

      toast({
        title: "Successfully Registered",
        description: `Your encrypted number has been submitted to the lottery.`,
      });
      
      await loadLotteries();
    } catch (error) {
      console.error("Failed to register:", error);
      toast({
        title: "Registration Failed",
        description: error instanceof Error ? error.message : "Unknown error",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDrawWinner = async (lotteryId: bigint) => {
    if (!walletClient || !chainId) return;

    setIsLoading(true);
    try {
      const provider = new BrowserProvider(walletClient as any);
      const signer = await provider.getSigner();
      
      await drawWinner(signer, lotteryId, chainId);
      toast({
        title: "Winner Drawn",
        description: "The winner has been selected randomly.",
      });
      
      await loadLotteries();
    } catch (error) {
      console.error("Failed to draw winner:", error);
      toast({
        title: "Failed to Draw Winner",
        description: error instanceof Error ? error.message : "Unknown error",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDecryptWinner = async (lotteryId: bigint) => {
    if (!walletClient || !fhevmReady || !chainId) {
      toast({
        title: "Not Ready",
        description: "Please wait for FHEVM to initialize and ensure wallet is connected.",
        variant: "destructive",
      });
      return;
    }

    setDecryptingLotteryId(lotteryId);
    setIsLoading(true);
    try {
      const provider = new BrowserProvider(walletClient as any);
      const signer = await provider.getSigner();
      const { getContractAddress } = await import("@/lib/contract");
      const contractAddress = getContractAddress(chainId);
      
      if (!contractAddress) {
        throw new Error("Contract address not configured");
      }

      const fhevm = getFHEVMInstance();
      if (!fhevm) {
        throw new Error("FHEVM instance not available");
      }

      // Get winning number
      const winningNumber = await getWinningNumber(provider, lotteryId, chainId);
      
      // Decrypt (pass chainId for proper network handling)
      const decrypted = await decryptNumber(fhevm, winningNumber, contractAddress, signer, chainId);
      
      const lottery = lotteries.find(l => l.id === lotteryId);
      toast({
        title: "Winner Number Decrypted",
        description: `The winning number is: ${decrypted}. Winner: ${lottery?.winner.slice(0, 6)}...${lottery?.winner.slice(-4)}`,
      });
    } catch (error) {
      console.error("Failed to decrypt:", error);
      toast({
        title: "Decryption Failed",
        description: error instanceof Error ? error.message : "Unknown error",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
      setDecryptingLotteryId(null);
    }
  };

  const selectedLottery = selectedLotteryId 
    ? lotteries.find(l => l.id === selectedLotteryId)
    : null;

  return (
    <div className="min-h-screen bg-background">
      <Hero onCreateLottery={() => setCreateDialogOpen(true)} />
      
      <section className="container mx-auto px-4 py-16">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold mb-4 text-foreground">Active Lotteries</h2>
          <p className="text-muted-foreground text-lg">
            Participate in encrypted number lotteries
          </p>
        </div>
        
        {lotteries.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-muted-foreground">No lotteries available. Create one to get started!</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-7xl mx-auto">
            {lotteries.map((lottery) => (
              <DrawCard
                key={lottery.id.toString()}
                lottery={lottery}
                isWalletConnected={isConnected}
                isParticipant={false}
                onParticipate={() => handleParticipate(lottery.id)}
                onDrawWinner={() => handleDrawWinner(lottery.id)}
                onDecryptWinner={() => handleDecryptWinner(lottery.id)}
                canDraw={lottery.creator.toLowerCase() === address?.toLowerCase()}
                canDecrypt={lottery.isDrawn}
              />
            ))}
          </div>
        )}
      </section>
      
      <WishDialog
        open={wishDialogOpen}
        onOpenChange={setWishDialogOpen}
        lotteryName={selectedLottery?.name || "Lottery"}
        onSubmit={handleSubmitNumber}
        isLoading={isLoading}
      />

      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Create New Lottery</DialogTitle>
            <DialogDescription>
              Create a new encrypted number lottery
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="name">Lottery Name</Label>
              <Input
                id="name"
                placeholder="Enter lottery name"
                value={lotteryName}
                onChange={(e) => setLotteryName(e.target.value)}
                disabled={isLoading}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="maxParticipants">Max Participants</Label>
              <Input
                id="maxParticipants"
                type="number"
                min="1"
                placeholder="10"
                value={maxParticipants}
                onChange={(e) => setMaxParticipants(e.target.value)}
                disabled={isLoading}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setCreateDialogOpen(false)}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreateLottery}
              disabled={isLoading || !lotteryName || !maxParticipants}
            >
              {isLoading ? "Creating..." : "Create Lottery"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      <Footer />
    </div>
  );
};

export default Index;
